import { dtoToRgObject } from "@project-rouge/rg-aggregator";
import type { RgWorld, RgWorldDto } from "src/types/RgCorePackage";
import { Decompress } from "src/utils/Decompress";
import { World } from "src/types/World";

export const COST_FIXTURE_VERSION = 2 as const;

export interface CostFixture {
  version: typeof COST_FIXTURE_VERSION;
  id: string;
  label?: string;
  /** Free-text region tag entered at export time, e.g. "London". */
  area?: string;
  /** Captured from World.longitude/latitude — restored via setGeoOrigin on
   *  hydrate so the map can centre on the saved location. The rgWorld zip is
   *  local-meters geometry only and does not carry lng/lat. */
  lng?: number;
  lat?: number;
  capturedAt: string;
  rgWorldZipBase64: string;
}

export function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export function hydrateWorldFromFixture(fixture: CostFixture): World {
  // v1 fixtures lack lng/lat — accept them but the map can't centre on them.
  // v2 carries lng/lat and we restore via setGeoOrigin below.
  if (fixture.version !== COST_FIXTURE_VERSION && fixture.version !== 1) {
    throw new Error(
      `Unsupported cost fixture version ${fixture.version}; expected ${COST_FIXTURE_VERSION}`
    );
  }
  const zip = base64ToUint8Array(fixture.rgWorldZipBase64);
  const dto = Decompress<RgWorldDto>(zip);
  const rgWorld = dtoToRgObject<RgWorld>(dto);
  const world = new World({ rgWorld }, false);
  if (typeof fixture.lng === "number" && typeof fixture.lat === "number") {
    world.setGeoOrigin({ longitude: fixture.lng, latitude: fixture.lat });
  }
  return world;
}

export async function worldToFixture(
  world: World,
  opts: { id: string; label?: string; area?: string }
): Promise<CostFixture> {
  const zip = await world.getRgWorldZip();
  return {
    version: COST_FIXTURE_VERSION,
    id: opts.id,
    label: opts.label,
    area: opts.area,
    lng: world.longitude,
    lat: world.latitude,
    capturedAt: new Date().toISOString(),
    rgWorldZipBase64: uint8ArrayToBase64(zip),
  };
}
